import { Invoice, InvoiceDateKind, InvoiceListItem, InvoiceStatus, InvoiceStatusNotification, PaymentChannel, ReportType, Calculation, AccountingData } from "../../model";
import { DatelyObject, Dates, ODataQueryOptions, ODataResult } from "../../util";
import { Ajax, CrudApiBase } from "../infra";
import { Invoices } from "./Invoices";

/**
 * Payment channels are methods that hanlde salary payments:
 * Paytrail / Palkkaus.fi Customer funds account handling, Finvoice, Accounting software, Banks Web Services etc.
 * This controller is for those channels to fetch Invoices and other data as well as to update payment feedback back to Palkkaus.fi.
 * The services can only be used by registered payment channels: Ask us if you wish to register.
 */
export class PaymentChannelApi extends CrudApiBase<Invoice, InvoiceListItem> {

  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1"];

  /** Base URL is set by the constructor because it is using the paymentChannel as part of URL. */
  protected baseUrl: string = "";

  /** Creates a new API class for a given PaymentChannel */
  constructor(private paymentChannel: PaymentChannel, ajax: Ajax) {
    super(ajax);
    this.baseUrl = `/v03/api/payment-channel/${paymentChannel}`;
  }

  /**
   * NOTE: Direct Save is currently not supported.
   * Use createInvoices and updateStatus instead.
   * This method is only for compatibility with CrudApiBase and in expectation of future needs (non-salary invoices).
   * @param itemToSave - The item that is updated/inserted.
   * @throws Not supported
   */
  public save(itemToSave: Invoice): Promise<Invoice> { // eslint-disable-line @typescript-eslint/no-unused-vars
    throw new Error("Not supported.");
  }

  /**
   * Client-side (synchronous) method for getting a new blank item as bases for UI binding.
   * NOTE: At the moment, this method is pretty useless as you cannot save invoice items:
   * You need to create them using createInvoice() method based on a calculation.
   */
  public getBlank(): Invoice {
    const invoice = new Invoices(this.ajax).getBlank();
    invoice.header ??= {};
    invoice.header.channel = this.paymentChannel;
    return invoice;
  }

  /**
   * Gets invoices with their full payload - including PDF content - filtering by a status.
   * NOTE: This is designed mainly for long-running batch processes for reading unread Invoices.
   * They can read the items in one read. For browser process we suggest OData query (with list item)
   * and then fetching items one-by-one.
   * This is for more http-queries, but each taking a shorter time so you can use )
   * @param status Status filter for the query. Default is unread.
   * @param start Optional start date for the period to query the invoices. If not set, defaults to last 14 days.
   * @param end Optional end date for the period to query the invoices. If not set, today is used.
   * @param dateKind Optional date specifier for the period: invoicing date (default), due date or logical date (like salary date).
   * @returns Invoices which have been created in the Invoices queue.
   */
  public getInvoicesByStatus(status: InvoiceStatus = InvoiceStatus.Unread, start: DatelyObject | null = null, end: DatelyObject | null = null, dateKind: InvoiceDateKind = InvoiceDateKind.InvoiceDate): Promise<Invoice[]> {
    if (this.paymentChannel) {
      const startDate = Dates.asDate(start);
      const endDate = Dates.asDate(end);
      const url = `${this.baseUrl}/status/${status}?dateKind=${dateKind}&start=${startDate || ""}&end=${endDate || ""}`;
      return this.ajax.getJSON(url);
    } else {
      return Promise.resolve<Invoice[]>([]);
    }
  }

  /**
   * Makes an OData query to the service's main OData method.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public getOData(query: ODataQueryOptions | string): Promise<ODataResult<any>> {
    if (this.paymentChannel) {
      return super.getOData(query);
    }
    return Promise.resolve<ODataResult<any>>({items: [], count: 0});
  }

  /**
   * Gets a single item based on identier
   * @param id - Unique identifier for the object
   * @returns A Promise with result data.
   */
  public getSingle(id: string): Promise<Invoice> {
    if (this.paymentChannel) {
      return super.getSingle(id);
    }
    return Promise.resolve<any>(undefined);
  }

  /**
   * Updates the payment statuses of invoices.
   * @param data Array of invoices to update.
   */
  public updateStatus(data: InvoiceStatusNotification[]): Promise<Invoice[]> {
    const url = `${this.baseUrl}/update-status`;
    return this.ajax.postJSON(url, data);
  }

  /**
   * Returns content (PDF) url for an invoice.
   * @param id The id of the invoice.
   * @param inline If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   */
  public getContentUrl(id: string, inline = false): string {
    return `${this.ajax.getServerAddress()}${this.baseUrl}/${id}/content?inline=${inline}`
      + `&access_token=${encodeURIComponent(this.ajax.getCurrentToken()!)}`;
  }
  /**
   * Gets content (e.g. PDF) for an invoice using the invoice ID.
   * @param channel - The payment channel to use
   * @param id - The ID of the invoice to get content for
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the content
   */
  public getContent(channel: PaymentChannel, id: string, inline?: boolean): Promise<Blob>;
  /**
   * Gets content (e.g. PDF) for multiple invoices using their IDs.
   * @param channel - The payment channel to use
   * @param ids - Array of invoice IDs to get content for
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the content
   */
  public getContent(channel: PaymentChannel, ids: string[], inline?: boolean): Promise<Blob>;
  /**
   * Gets content (e.g. PDF) by posting the invoice object.
   * @param channel - The payment channel to use
   * @param invoice - The Invoice object to get content for
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the content
   */
  public getContent(channel: PaymentChannel, invoice: Invoice, inline?: boolean): Promise<Blob>;
  /**
   * Implementation of getContent() that handles string ID, string[] IDs and Invoice object inputs.
   * @param channel - The payment channel to use
   * @param idOrIdsOrInvoice - Either an invoice ID string, array of IDs, or Invoice object
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download). Defaults to false.
   * @returns A Promise that resolves to a Blob containing the content
   */
  public getContent(channel: PaymentChannel, idOrIdsOrInvoice: unknown, inline = false): Promise<Blob> {
    if((idOrIdsOrInvoice as Invoice)?.id) {
      return this.ajax.postBlob(`${this.baseUrl}/${channel}/content?inline=${inline}`, idOrIdsOrInvoice).then(response => response.data);
    }
    if(Array.isArray(idOrIdsOrInvoice)) {
      const idsPart = idOrIdsOrInvoice.map(x => "ids=" + encodeURIComponent("" + x)).join("&");
      return this.ajax.getBlob(`${this.baseUrl}/${channel}/content?${idsPart}&inline=${inline}`).then(response => response.data);
    } else {
      return this.ajax.getBlob(`${this.baseUrl}/${channel}/${idOrIdsOrInvoice}/content?inline=${inline}`).then(response => response.data);
    }
  }

  /**
   * Returns sepa url for an invoice.
   * @param channel The payment channel to use.
   * @param id The id of the invoice.
   * @param inline If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   */
  public getSepaUrl(channel: PaymentChannel, id: string, inline = false): string {
    return `${this.ajax.getServerAddress()}${this.baseUrl}/${channel}/${id}/sepa?inline=${inline}`
      + `&access_token=${encodeURIComponent(this.ajax.getCurrentToken()!)}`;
  }


  /**
   * Gets SEPA content for a single invoice using the invoice ID.
   * @param channel - The payment channel to use
   * @param id - The ID of the invoice to get SEPA content for
   * @param payerIbanNumber - Optional IBAN number of the payer
   * @param bankPartyId - Optional bank party ID
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the SEPA content
   */
  public getSepa(channel: PaymentChannel, id: string, payerIbanNumber?: string, bankPartyId?: string, inline?: boolean): Promise<Blob>;

  /**
   * Gets SEPA content for multiple invoices using their IDs.
   * @param channel - The payment channel to use
   * @param ids - Array of invoice IDs to get SEPA content for
   * @param payerIbanNumber - Optional IBAN number of the payer
   * @param bankPartyId - Optional bank party ID
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the SEPA content
   */
  public getSepa(channel: PaymentChannel, ids: string[], payerIbanNumber?: string, bankPartyId?: string, inline?: boolean): Promise<Blob>;

  /**
   * Gets SEPA content by posting the invoice object.
   * @param channel - The payment channel to use
   * @param invoice - The Invoice object to get SEPA content for
   * @param payerIbanNumber - Optional IBAN number of the payer
   * @param bankPartyId - Optional bank party ID
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the SEPA content
   */
  public getSepa(channel: PaymentChannel, invoice: Invoice, payerIbanNumber?: string, bankPartyId?: string, inline?: boolean): Promise<Blob>;

  /**
   * Implementation of getSepa() that handles string ID, string[] IDs, and Invoice object inputs.
   * @param channel - The payment channel to use
   * @param idOrIdsOrInvoice - Either an invoice ID string, array of IDs, or Invoice object
   * @param payerIbanNumber - Optional IBAN number of the payer
   * @param bankPartyId - Optional bank party ID
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download). Defaults to false.
   * @returns A Promise that resolves to a Blob containing the SEPA content
   */
  public getSepa(channel: PaymentChannel, idOrIdsOrInvoice: unknown, payerIbanNumber?: string, bankPartyId?: string, inline = false): Promise<Blob> {
    const getPayerIbanNumber = payerIbanNumber ? `&payerIbanNumber=${encodeURIComponent(payerIbanNumber)}` : "";
    const getBankPartyId = bankPartyId ? `&bankPartyId=${encodeURIComponent(bankPartyId)}` : "";
    if((idOrIdsOrInvoice as Invoice)?.id) {
      return this.ajax.postBlob(`${this.baseUrl}/${channel}/sepa?inline=${inline}` + getPayerIbanNumber + getBankPartyId, idOrIdsOrInvoice).then(response => response.data);
    }
    if(Array.isArray(idOrIdsOrInvoice)) {
      const idsPart = idOrIdsOrInvoice.map(x => "ids=" + encodeURIComponent("" + x)).join("&");
      return this.ajax.getBlob(`${this.baseUrl}/${channel}/sepa?${idsPart}&inline=${inline}` + getPayerIbanNumber + getBankPartyId).then(response => response.data);
    } else {
      return this.ajax.getBlob(`${this.baseUrl}/${channel}/${idOrIdsOrInvoice}/sepa?inline=${inline}` + getPayerIbanNumber + getBankPartyId).then(response => response.data);
    }
  }

  /**
   * Gets a URL for accessing a report for a single invoice.
   * @param channel - The payment channel to use
   * @param id - The ID of the invoice to get the report URL for
   * @param type - The type of report to generate
   * @param isSensitive - Indicates if the report contains sensitive information
   * @param target - The target for the report
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A URL string for accessing the report
   */
  public getReportUrl(channel: PaymentChannel, id: string, type: ReportType.SalarySlip | ReportType.EmployerReport | ReportType.PaymentReport | ReportType.AccountingReport | ReportType.Invoice, isSensitive?: boolean, target?: string, inline?: boolean): string {
    const getIsSensitive = isSensitive ? "&isSensitive=true" : "";
    const getTarget = target ? "&target=" + encodeURIComponent(target) : "";
    return `${this.ajax.getServerAddress()}${this.baseUrl}/${channel}/${id}/${type}?inline=${inline}` + getIsSensitive + getTarget
      + `&access_token=${encodeURIComponent(this.ajax.getCurrentToken()!)}`;
  }

  /**
   * Gets a report for a single invoice using the invoice ID.
   * @param channel - The payment channel to use
   * @param id - The ID of the invoice to get the report for
   * @param type - The type of report to generate
   * @param isSensitive - Indicates if the report contains sensitive information
   * @param target - The target for the report
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the report
   */
  public getReport(channel: PaymentChannel, id: string, type: ReportType.SalarySlip | ReportType.EmployerReport | ReportType.PaymentReport | ReportType.AccountingReport | ReportType.Invoice, isSensitive?: boolean, target?: string, inline?: boolean): Promise<Blob>;

  /**
   * Gets a report for multiple invoices using their IDs.
   * @param channel - The payment channel to use
   * @param ids - Array of invoice IDs to get the report for
   * @param type - The type of report to generate
   * @param isSensitive - Indicates if the report contains sensitive information
   * @param target - The target for the report
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the report
   */
  public getReport(channel: PaymentChannel, ids: string[], type: ReportType.SalarySlip | ReportType.EmployerReport | ReportType.PaymentReport | ReportType.AccountingReport | ReportType.Invoice, isSensitive?: boolean, target?: string, inline?: boolean): Promise<Blob>;

  /**
   * Implementation of getReport() that handles string ID and string[] IDs inputs.
   * @param channel - The payment channel to use
   * @param idOrIds - Either an invoice ID string or array of IDs
   * @param type - The type of report to generate
   * @param isSensitive - Indicates if the report contains sensitive information
   * @param target - The target for the report
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download). Defaults to false.
   * @returns A Promise that resolves to a Blob containing the report
   */
  public getReport(channel: PaymentChannel, idOrIds: unknown, type: ReportType.SalarySlip | ReportType.EmployerReport | ReportType.PaymentReport | ReportType.AccountingReport | ReportType.Invoice, isSensitive?: boolean, target?: string, inline = false): Promise<Blob> {
    const getIsSensitive = isSensitive ? "&isSensitive=true" : "";
    const getTarget = target ? "&target=" + encodeURIComponent(target) : "";
    if (Array.isArray(idOrIds)) {
      const idsPart = idOrIds.map(x => "ids=" + encodeURIComponent("" + x)).join("&");
      return this.ajax.getBlob(`${this.baseUrl}/${channel}/${type}?${idsPart}&inline=${inline}` + getIsSensitive + getTarget).then(response => response.data);
    } else {
      return this.ajax.getBlob(`${this.baseUrl}/${channel}/${idOrIds}/${type}?inline=${inline}` + getIsSensitive + getTarget).then(response => response.data);
    }
  }

  /**
   * Gets an accounting report for a single invoice using the invoice ID.
   * @param channel - The payment channel to use
   * @param id - The ID of the invoice to get the accounting report for
   * @param target - The target for the report
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the accounting report
   */
  public getAccounting(channel: PaymentChannel, id: string, target?: string, inline?: boolean): Promise<AccountingData>;

  /**
   * Gets an accounting report for multiple invoices using their IDs.
   * @param channel - The payment channel to use
   * @param ids - Array of invoice IDs to get the accounting report for
   * @param target - The target for the report
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the accounting report
   */
  public getAccounting(channel: PaymentChannel, ids: string[], target?: string, inline?: boolean): Promise<AccountingData>;

  /**
   * Implementation of getAccounting() that handles string ID and string[] IDs inputs.
   * @param channel - The payment channel to use
   * @param idOrIds - Either an invoice ID string or array of IDs
   * @param target - The target for the report
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download). Defaults to false.
   * @returns A Promise that resolves to a Blob containing the accounting report
   */
  public getAccounting(channel: PaymentChannel, idOrIds: unknown, target?: string, inline = false): Promise<AccountingData> {
    const getTarget = target ? "&target=" + encodeURIComponent(target) : "";
    if (Array.isArray(idOrIds)) {
      const idsPart = idOrIds.map(x => "ids=" + encodeURIComponent("" + x)).join("&");
      return this.ajax.getJSON(`${this.baseUrl}/${channel}/accounting?${idsPart}&inline=${inline}` + getTarget);
    } else {
      return this.ajax.getJSON(`${this.baseUrl}/${channel}/${idOrIds}/accounting?inline=${inline}` + getTarget);
    }
  }
  /**
   * Implementation of getCalculation() that handles string ID and Calculation object inputs.
   * @param channel - The payment channel to use
   * @param id - The ID of the invoice to get the calculation for
   * @returns A Promise that resolves to a Calculation object
   */
  public getCalculation(channel: PaymentChannel, id: string): Promise<Calculation> {
    return this.ajax.getJSON(`${this.baseUrl}/${channel}/${id}/calculation`);
  }

  /**
   * Implementation of getCalculations() that handles string[] IDs and Calculation[] inputs.
   * @param channel - The payment channel to use
   * @param ids - Array of invoice IDs to get the calculation for
   * @returns A Promise that resolves to an array of Calculation objects
   */
  public getCalculations(channel: PaymentChannel, ids: string[]): Promise<Calculation[]> {
    const _ids = ids.map(x => "ids=" + encodeURIComponent("" + x)).join("&");
    return this.ajax.getJSON(`${this.baseUrl}/${channel}/calculation?${_ids}`);
  }
}